// Course settings guide — the ⋮ menu's Global Config modal (GlobalConfig),
// plus the training-level menu actions Create New, Generate in Different Ratio
// (RatioReproduceModal) and Language. Locale-keyed like every guide
// (content.<lang> → { feature, title, sections }).

const courseSettingsGuide = {
  id: "course-settings",
  icon: "settings",
  keywords: [
    "global config",
    "genel konfigürasyon",
    "settings",
    "ayarlar",
    "course settings",
    "eğitim ayarları",
    "title",
    "başlık",
    "author",
    "yazar",
    "aspect ratio",
    "en boy oranı",
    "ratio",
    "oran",
    "theme",
    "tema",
    "palette",
    "palet",
    "font",
    "yazı tipi",
    "learning time",
    "öğrenme zamanı",
    "visor navigation",
    "visor gezintisi",
    "create new",
    "yeni oluştur",
    "different ratio",
    "farklı oranda üret",
    "language",
    "dil",
    "configuración global",
    "configuración del curso",
    "ajustes",
    "título",
    "autor",
    "proporción",
    "relación de aspecto",
    "paleta",
    "fuente",
    "tiempo de aprendizaje",
    "navegación del visor",
    "crear nuevo",
    "otra proporción",
    "idioma",
    "course mode",
    "eğitim modu",
    "modo del curso",
    "clm",
  ],
  content: {
    en: {
      feature: "Course settings",
      title: "Course settings (Global Config)",
      sections: [
        {
          id: "open",
          title: "Opening Global Config",
          videoUrl: null,
          text:
            "Open the **⋮** menu at the right end of the top navbar and choose **Global Config**. The **Global Configuration** window groups the training-wide settings into cards.\n\n" +
            "Press **Save changes** to apply them or **Cancel** to discard. If you close the window with unsaved edits, it asks whether to save them.",
        },
        {
          id: "general-look",
          title: "General, geometry, theme & font",
          videoUrl: null,
          text:
            "- **General** — **Lesson Title**, **Description**, **Author** and **Course mode** (CLM, Infographic, Interactive HTML, Presentation, Atomic Presentation or Mixed).\n" +
            "- **Scene geometry** — the screen ratio: **16:9** (Standard / Digital), **4:3** (Classic), **1:1** (Square / Social), **9:16** (Reels / Shorts) or **4:5** (Instagram Portrait).\n" +
            "- **Theme** — **Select** opens **Theme & Palette Selection** to choose a theme and color palette, optionally with **Apply to all scenes**.\n" +
            "- **Training Font** — **Select** opens a font list inside the card. The font is applied to every Basic Text and Rich Text box in the whole training, replacing per-box font settings, when you save.\n\n" +
            "Changing the ratio of an existing training does not modify it in place: saving opens **Save As**, and a new copy is created in the new ratio with its elements adapted. The original keeps its ratio.\n\n" +
            "**CLM** (in beta) is for static CLM presentations: the element library, templates and paste only offer what a CLM package can hold — images, text, click zones, animated HTML and shapes — and **Export...** gets a **CLM** package format (see the **Save, preview & export** guide).",
        },
        {
          id: "behaviour",
          title: "Scene defaults, progress & navigation",
          videoUrl: null,
          text:
            "**Scene defaults** sets the default duration, mandatory rules and scene transitions for every scene — see the **Scenes** guide (scene settings) for details.\n\n" +
            "**Progress & access**:\n\n" +
            "- **Typical learning time** (hours and minutes) and **Minimum time per page** (seconds).\n" +
            "- **Async Widget** — adds the background-job tray to the launcher button at the bottom right of the editor.\n" +
            "- **Chatbot** — adds the AI chat assistant to the same launcher.\n" +
            "- **Timeline Visible** — shows or hides the timeline under the canvas.\n" +
            "- **Required Question Evaluation** — the learner must answer at least **Required Correct Answers** questions correctly (out of **Total Question Number**) to pass.\n\n" +
            "**Visor navigation** controls what learners get in the player: **Button bar**, **Side navbar** and **Keyboard arrows**.",
        },
        {
          id: "create-new",
          title: "Create New",
          videoUrl: null,
          text: "**⋮ → Create New** opens the same configuration window for a brand-new training. Set its title, ratio and other settings, then save to create a new, empty training with those settings. The training you have open is not changed.",
        },
        {
          id: "different-ratio",
          title: "Generate in Different Ratio",
          videoUrl: null,
          text:
            "**⋮ → Generate in Different Ratio** reproduces the whole training in another screen format with AI:\n\n" +
            "- **Select Target Ratio** — 16:9, 4:3, 1:1, 9:16 or 4:5.\n" +
            "- **Resolution** — **1K** (low cost, fast), **2K** (default, balanced) or **4K** (maximum quality).\n" +
            "- **New Training Name** — suggested as the current title plus the ratio; you can edit it.\n\n" +
            "Press **Create**. The job runs in the background with a progress notice at the bottom right and can take a few minutes. It always creates a **new training** (find it on your Trainings page); the original stays untouched. Images may not stay exactly the same.",
        },
        {
          id: "language",
          title: "Interface language",
          videoUrl: null,
          text: "**⋮ → Language** switches the editor's interface between **English** and **Turkish**. It changes only the menus and labels — your training's content is not translated.",
        },
      ],
    },
    tr: {
      feature: "Eğitim Ayarları",
      title: "Eğitim ayarları (Genel Konfigürasyon)",
      sections: [
        {
          id: "open",
          title: "Genel Konfigürasyon'u açma",
          videoUrl: null,
          text:
            "Üst gezinme çubuğunun sağ ucundaki **⋮** menüsünü açın ve **Genel Konfigürasyon**'u seçin. Açılan pencere, eğitim genelindeki ayarları kartlar halinde gruplar.\n\n" +
            "Uygulamak için **Değişiklikleri Kaydet**'e, vazgeçmek için **İptal**'e basın. Kaydedilmemiş değişikliklerle pencereyi kapatırsanız kaydetmek isteyip istemediğiniz sorulur.",
        },
        {
          id: "general-look",
          title: "Genel bilgiler, geometri, tema ve font",
          videoUrl: null,
          text:
            "- **Genel Bilgiler** — **Eğitim Başlığı**, **Tanım**, **Yazar** ve **Eğitim Modu** (CLM, İnfografik, Etkileşimli HTML, Sunum, Atomik Sunum veya Karışık).\n" +
            "- **Sahne Geometrisi** — ekran oranı: **16:9** (Standart / Dijital), **4:3** (Klasik), **1:1** (Kare / Sosyal), **9:16** (Reels / Shorts) veya **4:5** (Instagram Portre).\n" +
            "- **Tema** — **Seç** düğmesi **Tema & Palet Seçimi**'ni açar; buradan bir tema ve renk paleti seçer, isterseniz **Tüm sahnelere uygula** seçeneğini işaretlersiniz.\n" +
            "- **Eğitim Fontu** — **Seç** düğmesi font listesini kartın içinde açar. Seçilen font, kaydettiğinizde eğitimdeki tüm Basit Metin ve Zengin Metin kutularına uygulanır ve kutu bazındaki font ayarlarının yerini alır.\n\n" +
            "Mevcut bir eğitimin oranını değiştirmek eğitimi yerinde değiştirmez: kaydettiğinizde **Farklı Kaydet** açılır ve öğeleri yeni orana uyarlanmış yeni bir kopya oluşturulur. Orijinal eğitim kendi oranında kalır.\n\n" +
            "**CLM** (beta aşamasında) statik CLM sunumları içindir: öğe kütüphanesi, şablonlar ve yapıştırma yalnızca bir CLM paketinin içerebileceği öğeleri — görseller, metin, tıklama alanları, animasyonlu HTML ve şekiller — sunar; **Yayımla...** penceresine de **CLM** paket biçimi eklenir (**Kaydetme, önizleme ve dışa aktarma** rehberine bakın).",
        },
        {
          id: "behaviour",
          title: "Sahne varsayılanları, ilerleme ve gezinti",
          videoUrl: null,
          text:
            "**Sahne Varsayılanları**, tüm sahneler için varsayılan süreyi, zorunluluk kurallarını ve sahne geçişlerini belirler — ayrıntılar için **Sahneler** rehberine (sahne ayarları) bakın.\n\n" +
            "**İlerleme & Erişim**:\n\n" +
            "- **Standart Öğrenme Zamanı** (saat ve dakika) ve **Her bir sayfa için minimum süre** (saniye).\n" +
            "- **İş Kuyruğu** — editörün sağ altındaki başlatıcı düğmeye arka plan işleri panelini ekler.\n" +
            "- **Sohbet Botu** — aynı başlatıcıya yapay zeka sohbet asistanını ekler.\n" +
            "- **Zaman Çizelgesi** — tuvalin altındaki zaman çizelgesini gösterir veya gizler.\n" +
            "- **Zorunlu Değerlendirme Sorusu** — öğrencinin geçmesi için **Toplam Soru Sayısı** içinden en az **Tamamlanması Gereken Doğru Soru Sayısı** kadar soruyu doğru yanıtlaması gerekir.\n\n" +
            "**Visor Gezintisi**, öğrencinin oynatıcıda neleri kullanabileceğini belirler: **Düğme Çubuğu**, **Yan gezinti çubuğu** ve **Klavye Ok Tuşları**.",
        },
        {
          id: "create-new",
          title: "Yeni Oluştur",
          videoUrl: null,
          text: "**⋮ → Yeni Oluştur**, aynı yapılandırma penceresini yepyeni bir eğitim için açar. Başlığı, oranı ve diğer ayarları belirleyip kaydettiğinizde bu ayarlarla yeni ve boş bir eğitim oluşturulur. Açık olan eğitiminiz değişmez.",
        },
        {
          id: "different-ratio",
          title: "Farklı Oranda Üret",
          videoUrl: null,
          text:
            "**⋮ → Farklı Oranda Üret**, eğitimin tamamını yapay zeka ile başka bir ekran formatında yeniden üretir:\n\n" +
            "- **Hedef Oran Seçin** — 16:9, 4:3, 1:1, 9:16 veya 4:5.\n" +
            "- **Çözünürlük** — **1K** (düşük maliyet, hızlı), **2K** (varsayılan, dengeli) veya **4K** (maksimum kalite).\n" +
            "- **Yeni Eğitim Adı** — mevcut başlık ve oran olarak önerilir; değiştirebilirsiniz.\n\n" +
            "**Oluştur**'a basın. İş arka planda çalışır, sağ altta ilerleme bildirimi görünür ve birkaç dakika sürebilir. Her zaman **yeni bir eğitim** oluşturur (Eğitimleriniz sayfasında bulabilirsiniz); orijinal eğitime dokunulmaz. Görseller tamamen aynı kalmayabilir.",
        },
        {
          id: "language",
          title: "Arayüz dili",
          videoUrl: null,
          text: "**⋮ → Dil**, editör arayüzünü **İngilizce** ve **Türkçe** arasında değiştirir. Yalnızca menüler ve etiketler değişir — eğitiminizin içeriği çevrilmez.",
        },
      ],
    },
    es: {
      feature: "Configuración del curso",
      title: "Configuración del curso (Configuración Global)",
      sections: [
        {
          id: "open",
          title: "Abrir Configuración Global",
          videoUrl: null,
          text:
            "Abre el menú **⋮** del extremo derecho de la barra de navegación superior y elige **Configuración Global**. La ventana **Configuración Global** agrupa en tarjetas los ajustes que afectan a toda la formación.\n\n" +
            "Pulsa **Guardar Cambios** para aplicarlos o **Cancelar** para descartarlos. Si cierras la ventana con cambios sin guardar, te pregunta si quieres guardarlos.",
        },
        {
          id: "general-look",
          title: "General, geometría, tema y fuente",
          videoUrl: null,
          text:
            "- **General**: **Título del Curso**, **Descripción**, **Autor** y **Modo del curso** (CLM, Infografía, HTML interactivo, Presentación, Presentación atómica o Mixto).\n" +
            "- **Geometría de escena**: la proporción de pantalla: **16:9** (Estándar / Digital), **4:3** (Clásico), **1:1** (Cuadrado / Redes), **9:16** (Reels / Shorts) o **4:5** (Instagram Vertical).\n" +
            "- **Tema**: **Seleccionar** abre **Selección de Tema y Paleta** para elegir un tema y una paleta de colores, con la opción **Aplicar a todas las escenas**.\n" +
            "- **Fuente de la Formación**: **Seleccionar** abre una lista de fuentes dentro de la tarjeta. Al guardar, la fuente se aplica a todos los cuadros de texto básico y de texto enriquecido de toda la formación, y sustituye la fuente configurada en cada cuadro.\n\n" +
            "Cambiar la proporción de una formación existente no la modifica directamente: al guardar se abre **Guardar Como** y se crea una copia nueva en la nueva proporción, con sus elementos adaptados. La original conserva su proporción.\n\n" +
            "**CLM** (en fase beta) sirve para presentaciones CLM estáticas: la biblioteca de elementos, las plantillas y el pegado solo ofrecen lo que puede contener un paquete CLM (imágenes, texto, zonas de clic, HTML animado y formas), y **Exportar...** incluye el formato de paquete **CLM** (consulta la guía **Guardar, vista previa y exportar**).",
        },
        {
          id: "behaviour",
          title: "Valores por defecto de escena, progreso y navegación",
          videoUrl: null,
          text:
            "**Valores por defecto de escena** establece la duración predeterminada, las reglas de obligatoriedad y las transiciones de escena para todas las escenas; consulta la guía **Escenas** (ajustes de escena) para más detalles.\n\n" +
            "**Progreso y acceso**:\n\n" +
            "- **Tiempo típico de aprendizaje** (horas y minutos) y **Mínimo tiempo por página** (segundos).\n" +
            "- **Async Widget**: añade la bandeja de trabajos en segundo plano al botón lanzador de la esquina inferior derecha del editor.\n" +
            "- **Chatbot**: añade el asistente de chat con IA al mismo lanzador.\n" +
            "- **Línea de tiempo visible**: muestra u oculta la línea de tiempo debajo del lienzo.\n" +
            "- **Evaluación de preguntas requerida**: para aprobar, el alumno debe responder correctamente al menos el **Número de respuestas correctas requeridas** (sobre el **Número total de preguntas**).\n\n" +
            "**Navegación del visor** controla lo que el alumno tiene en el reproductor: **Barra de Botones**, **Barra Lateral** y **Flechas Del Teclado**.",
        },
        {
          id: "create-new",
          title: "Crear nuevo",
          videoUrl: null,
          text: "**⋮ → Crear nuevo** abre la misma ventana de configuración para una formación completamente nueva. Define su título, su proporción y los demás ajustes, y guarda para crear una formación nueva y vacía con esos ajustes. La formación que tienes abierta no cambia.",
        },
        {
          id: "different-ratio",
          title: "Generar en Otra Proporción",
          videoUrl: null,
          text:
            "**⋮ → Generar en Otra Proporción** reproduce toda la formación en otro formato de pantalla con IA:\n\n" +
            "- **Selecciona la Proporción Objetivo**: 16:9, 4:3, 1:1, 9:16 o 4:5.\n" +
            "- **Resolución**: **1K** (bajo costo, rápido), **2K** (predeterminado, equilibrado) o **4K** (máxima calidad).\n" +
            "- **Nombre de la Nueva Formación**: se propone el título actual más la proporción; puedes editarlo.\n\n" +
            "Pulsa **Crear**. El trabajo se ejecuta en segundo plano con un aviso de progreso abajo a la derecha y puede tardar unos minutos. Siempre crea una **formación nueva** (la encontrarás en tu página de Formaciones); la original no se modifica. Puede que las imágenes no queden exactamente iguales.",
        },
        {
          id: "language",
          title: "Idioma de la interfaz",
          videoUrl: null,
          text: "**⋮ → Idioma** cambia la interfaz del editor a **Inglés** o **Turco**; este menú no ofrece la opción de español. Solo cambian los menús y las etiquetas: el contenido de tu formación no se traduce.",
        },
      ],
    },
  },
};

export default courseSettingsGuide;
